import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapView } from "../components/MapView";
import { MapPin, Calendar, Clock, Tag, ArrowLeft, Heart, Repeat } from "lucide-react";
import { formatDateIt, zoneOf, placeOf, isExtraZone } from "../lib/constants";
import { toast } from "sonner";
import { TonightBadge } from "../components/TonightBadge";
import { ShareButtons } from "../components/ShareButtons";
import { usePageMeta } from "../lib/seo";

const EventLocation = ({ event }) => {
  const events = useMemo(() => [event], [event]);
  const center = useMemo(() => [event.latitude, event.longitude], [event.latitude, event.longitude]);
  return <MapView events={events} center={center} zoom={15} height="360px" />;
};

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  usePageMeta(event && {
    title: `${event.title}${placeOf(event) ? ` a ${zoneOf(event) || event.venue_name || "Milano"}` : ""} | WYD`,
    description: (event.description || `${event.category} a ${zoneOf(event) || "Milano"}`).slice(0, 160),
    image: event.image_url ? new URL(mediaUrl(event.image_url), window.location.origin).href : undefined,
  });
  const [notFound, setNotFound] = useState(false);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!user) return;
    api.get("/favorites").then((r) => setIsFav(r.data.some((e) => e.id === id))).catch(() => {});
  }, [user, id]);

  const toggleFav = async () => {
    if (!user) { toast.error("Accedi per salvare i preferiti"); navigate("/login"); return; }
    try {
      if (isFav) { await api.delete(`/favorites/${id}`); setIsFav(false); }
      else { await api.post(`/favorites/${id}`); setIsFav(true); toast.success("Aggiunto ai preferiti"); }
    } catch { toast.error("Operazione fallita"); }
  };

  if (notFound) return <div className="pt-32 text-center text-wyd-muted">Evento non trovato. <Link to="/" className="text-wyd-accent">Torna alla home</Link></div>;
  if (!event) return <div className="pt-32 flex justify-center"><div className="w-8 h-8 rounded-full border-2 border-wyd-accent border-t-transparent animate-spin" /></div>;

  const img = mediaUrl(event.image_url) || "https://static.prod-images.emergentagent.com/jobs/0318cc04-21bf-446a-8df6-374fe4adb994/images/75804f2344241a9c7d1f939d0784a62a8eb3d108fda6e83123d409f5cdb1bd9f.png";

  return (
    <div>
      <div className="relative h-[55vh] min-h-[380px]">
        <img src={img} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-wyd-bg via-wyd-bg/50 to-wyd-bg/20" />
        <div className="relative max-w-7xl mx-auto px-5 h-full flex flex-col justify-end pb-10">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="self-start mb-6 flex items-center gap-2 text-wyd-muted hover:text-white transition-colors duration-200 text-sm">
            <ArrowLeft size={16} /> Indietro
          </button>
          <div className="flex flex-wrap gap-2 self-start">
            <span className="overline text-wyd-bg bg-wyd-accent/95 px-3 py-1 rounded-full">{event.category}</span>
            <TonightBadge date={event.date} testid="detail-tonight-badge" />
          </div>
          <h1 data-testid="event-title" className="font-display font-black text-4xl md:text-5xl mt-4 max-w-3xl leading-tight">{event.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-wyd-muted text-lg leading-relaxed whitespace-pre-line">{event.description}</p>
          <div className="mt-10">
            <h2 className="font-display font-bold text-xl mb-4">Dove</h2>
            <EventLocation event={event} />
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="glass rounded-2xl p-6 sticky top-24 space-y-5">
            <Info icon={<Calendar size={18} />} label="Data" value={formatDateIt(event.date)} />
            {event.time && <Info icon={<Clock size={18} />} label="Orario" value={event.end_time ? `${event.time} – ${event.end_time}` : event.time} />}
            {event.recurrence_label && <NextDates event={event} />}
            {zoneOf(event) && <Info icon={<MapPin size={18} />} label={isExtraZone(event.neighborhood) ? "Zona" : "Quartiere"} value={zoneOf(event)} />}
            {event.venue_name && <Info icon={<MapPin size={18} />} label="Locale" value={event.venue_name} />}
            {event.address && (
              <a data-testid="event-address-link" target="_blank" rel="noopener noreferrer"
                href={`https://www.openstreetmap.org/?mlat=${event.latitude}&mlon=${event.longitude}#map=18/${event.latitude}/${event.longitude}`}
                className="block hover:opacity-80 transition-opacity duration-200">
                <Info icon={<MapPin size={18} />} label="Indirizzo" value={event.address} />
              </a>
            )}
            <Info icon={<Tag size={18} />} label="Prezzo" value={event.price || "Gratis"} />
            <button
              data-testid="detail-favorite-btn"
              onClick={toggleFav}
              className={`w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors duration-200 ${
                isFav ? "bg-wyd-accent text-wyd-bg" : "border border-wyd-accent text-wyd-accent hover:bg-wyd-accent hover:text-wyd-bg"
              }`}
            >
              <Heart size={18} className={isFav ? "fill-white" : ""} /> {isFav ? "Salvato nei preferiti" : "Aggiungi ai preferiti"}
            </button>
            <ShareButtons event={event} />
          </div>
        </aside>
      </div>
    </div>
  );
}

const Info = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="text-wyd-accent">{icon}</div>
    <div>
      <div className="overline text-wyd-muted">{label}</div>
      <div className="font-medium text-white">{value}</div>
    </div>
  </div>
);

const NextDates = ({ event }) => (
  <div data-testid="event-recurrence">
    <div className="flex items-center gap-2 text-wyd-accent font-semibold text-sm">
      <Repeat size={16} /> {event.recurrence_label}{event.time ? ` alle ${event.time}` : ""}
    </div>
    {event.next_dates?.length > 0 && (
      <ul className="mt-2 space-y-1 text-sm text-wyd-muted" data-testid="event-next-dates">
        {event.next_dates.map((d) => (
          <li key={d.date} className="flex justify-between gap-3"><span>{formatDateIt(d.date)}{d.title && d.title !== event.series_title ? ` · ${d.title}` : ""}</span><span>{d.time}</span></li>
        ))}
      </ul>
    )}
  </div>
);
